import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FaTimes, FaSave, FaSyncAlt } from 'react-icons/fa';
// Importe os estilos 'ui' do seu arquivo central.
import { ui } from '../utils/styles';

// Props esperadas:
// - show: boolean (controla visibilidade)
// - onClose: função (fecha o modal)
// - processo: objeto (dados do processo para edição/visualização)
// - onSave: função (callback para recarregar a lista de processos)
// - mode: 'create', 'edit', 'view'
// - clientesContratantes: array (lista de clientes para o dropdown de vínculo)
// - devedores: array (lista de devedores para o dropdown de vínculo)

const ProcessoModal = ({
    show, onClose, processo, onSave, mode, clientesContratantes, devedores
}) => {

    // --- Estados Locais do Formulário ---
    const [numeroProcesso, setNumeroProcesso] = useState('');
    const [tipoProcesso, setTipoProcesso] = useState('Judicial'); // Judicial, Administrativo, AcordoDireto
    const [dataInicio, setDataInicio] = useState('');
    const [status, setStatus] = useState('Aberto'); // Aberto, Fechado, Em Negociação, etc.
    const [observacao, setObservacao] = useState('');

    // Vínculos
    const [clienteContratanteId, setClienteContratanteId] = useState('');
    const [devedorId, setDevedorId] = useState('');

    const [loading, setLoading] = useState(false);

    const URL_BACKEND = process.env.REACT_APP_URL_BACKEND;
    const authHeader = useMemo(
        () => ({ Authorization: `Bearer ${localStorage.getItem('userToken')}` }),
        []
    );

    const tipoOptions = ['Judicial', 'Administrativo', 'Acordo Direto'];
    const statusOptions = ['Aberto', 'Fechado', 'Em Negociação', 'Concluído', 'Cancelado'];

    // --- Filtra Devedores com base no Cliente selecionado ---
    const filteredDevedores = useMemo(() => {
        if (!clienteContratanteId) {
            // Se nenhum cliente foi selecionado, mostra todos os devedores ou nenhum
            return devedores || [];
        }
        // Filtra apenas os devedores vinculados ao Cliente Contratante selecionado
        return devedores.filter(d => String(d.id_cliente_contratante) === String(clienteContratanteId));
    }, [devedores, clienteContratanteId]);

    // --- Efeitos de Inicialização e Reset ---
    useEffect(() => {
        if (show) {
            if (processo && (mode === 'edit' || mode === 'view')) {
                // Modo Edição ou Visualização
                setNumeroProcesso(processo.numero_processo || '');
                setTipoProcesso(processo.tipo_processo || 'Judicial');
                setDataInicio(processo.data_inicio ? processo.data_inicio.split('T')[0] : '');
                setStatus(processo.status || 'Aberto');
                setObservacao(processo.observacao || '');
                // Vínculos
                setClienteContratanteId(String(processo.id_cliente_contratante) || '');
                setDevedorId(String(processo.id_devedor) || '');

            } else {
                // Modo Criação
                setNumeroProcesso('');
                setTipoProcesso('Judicial');
                setDataInicio(new Date().toISOString().split('T')[0]); // Data atual como padrão
                setStatus('Aberto');
                setObservacao('');

                // Pré-seleciona o cliente logado se houver
                const storedEmitenteId = localStorage.getItem('userIdEmitente');
                setClienteContratanteId(storedEmitenteId ? String(storedEmitenteId) : '');

                setDevedorId('');
            }
        }
    }, [show, processo, mode]);

    // --- Reset Devedor se o Cliente mudar ---
    useEffect(() => {
        if (show && clienteContratanteId && filteredDevedores.length > 0) {
            // Se o devedor atualmente selecionado não estiver na nova lista filtrada, reseta-o
            const devedorExists = filteredDevedores.some(d => String(d.id) === String(devedorId));
            if (!devedorExists) {
                setDevedorId('');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clienteContratanteId, filteredDevedores]);


    // --- Lógica de Salvamento ---
    const handleSave = async (e) => {
        e.preventDefault();

        if (!clienteContratanteId || !devedorId || !dataInicio) {
            toast.error('Preencha o Cliente Contratante, o Devedor Principal e a Data de Início.');
            return;
        }

        const payload = {
            id_cliente_contratante: Number(clienteContratanteId),
            id_devedor: Number(devedorId),
            numero_processo: numeroProcesso || null, // Permite nulo/vazio para Acordo Direto
            tipo_processo: tipoProcesso,
            data_inicio: dataInicio,
            status,
            observacao,
            // Valor Total de Dívidas: Este campo será atualizado via outra API, não neste modal de CRUD simples.
        };

        setLoading(true);
        const toastId = toast.loading(mode === 'create' ? 'Criando Processo/Acordo...' : 'Atualizando Processo/Acordo...');

        try {
            if (mode === 'create') {
                await axios.post(`${URL_BACKEND}/processo`, payload, { headers: authHeader });
                toast.success('Processo/Acordo cadastrado com sucesso!', { id: toastId });
            } else if (mode === 'edit') {
                await axios.put(`${URL_BACKEND}/processo/${processo.id}`, payload, { headers: authHeader });
                toast.success('Processo/Acordo atualizado com sucesso!', { id: toastId });
            }

            onSave();
            onClose();
        } catch (error) {
            console.error('Erro ao salvar Processo/Acordo:', error);
            const msg = error.response?.data?.message || 'Erro ao salvar. Verifique os dados.';
            toast.error(msg, { id: toastId });
        } finally {
            setLoading(false);
        }
    };

    if (!show) return null;

    const isViewMode = mode === 'view';

    return (
        <div style={ui.modalOverlay}>
            <div style={{ ...ui.modalContent, maxWidth: 800 }}> {/* Modal um pouco maior */}

                {/* Cabeçalho do Modal */}
                <div style={ui.modalHeader}>
                    <h2 style={ui.modalTitle}>
                        {mode === 'create' ? 'Novo Processo / Acordo' : mode === 'edit' ? `Editar Processo #${processo?.id}` : `Visualizar Processo #${processo?.id}`}
                    </h2>
                    <button onClick={onClose} style={ui.modalCloseButton} disabled={loading}>
                        <FaTimes />
                    </button>
                </div>

                <form onSubmit={handleSave} style={ui.modalBody}>
                    <div style={ui.formGrid}>

                        {/* 1. Cliente Contratante (Vínculo Obrigatório) */}
                        <div style={ui.formGroup}>
                            <label style={ui.label} htmlFor="clienteContratante">Cliente (Contratante) *</label>
                            <select
                                id="clienteContratante"
                                style={ui.select}
                                value={clienteContratanteId}
                                onChange={e => setClienteContratanteId(e.target.value)}
                                disabled={isViewMode || loading}
                                required
                            >
                                <option value="">Selecione o Cliente Contratante...</option>
                                {clientesContratantes.map(c => (
                                    <option key={c.id} value={String(c.id)}>{c.nome}</option>
                                ))}
                            </select>
                        </div>

                        {/* 2. Devedor Principal (Vínculo Obrigatório) */}
                        <div style={ui.formGroup}>
                            <label style={ui.label} htmlFor="devedorPrincipal">Devedor Principal *</label>
                            <select
                                id="devedorPrincipal"
                                style={ui.select}
                                value={devedorId}
                                onChange={e => setDevedorId(e.target.value)}
                                disabled={isViewMode || loading || !clienteContratanteId}
                                required
                            >
                                <option value="">Selecione o Devedor...</option>
                                {filteredDevedores.map(d => (
                                    <option key={d.id} value={String(d.id)}>{d.nome} ({d.cnpj || d.cpf})</option>
                                ))}
                                {filteredDevedores.length === 0 && clienteContratanteId && (
                                    <option disabled>Nenhum devedor encontrado para este cliente.</option>
                                )}
                            </select>
                        </div>

                        {/* 3. Tipo de Processo */}
                        <div style={ui.formGroup}>
                            <label style={ui.label} htmlFor="tipoProcesso">Tipo de Processo/Acordo</label>
                            <select
                                id="tipoProcesso"
                                style={ui.select}
                                value={tipoProcesso}
                                onChange={e => setTipoProcesso(e.target.value)}
                                disabled={isViewMode || loading}
                            >
                                {tipoOptions.map(t => (
                                    <option key={t} value={t}>{t}</option>
                                ))}
                            </select>
                        </div>

                        {/* 4. Número do Processo (Opcional, mas pode ser obrigatório para Judicial) */}
                        <div style={ui.formGroup}>
                            <label style={ui.label} htmlFor="numeroProcesso">Número do Processo/Acordo</label>
                            <input
                                id="numeroProcesso"
                                style={ui.input}
                                value={numeroProcesso}
                                onChange={e => setNumeroProcesso(e.target.value)}
                                disabled={isViewMode || loading}
                                placeholder={tipoProcesso === 'Judicial' ? 'Nº CNJ (Obrigatório)' : 'Nº Interno (Opcional)'}
                            />
                        </div>

                        {/* 5. Data de Início */}
                        <div style={ui.formGroup}>
                            <label style={ui.label} htmlFor="dataInicio">Data de Início *</label>
                            <input
                                id="dataInicio"
                                type="date"
                                style={ui.input}
                                value={dataInicio}
                                onChange={e => setDataInicio(e.target.value)}
                                disabled={isViewMode || loading}
                                required
                            />
                        </div>

                        {/* 6. Status */}
                        <div style={ui.formGroup}>
                            <label style={ui.label} htmlFor="status">Status</label>
                            <select
                                id="status"
                                style={ui.select}
                                value={status}
                                onChange={e => setStatus(e.target.value)}
                                disabled={isViewMode || loading}
                            >
                                {statusOptions.map(s => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </div>

                        {/* 7. Observação (Full Width) */}
                        <div style={{ ...ui.formGroup, gridColumn: 'span 2' }}>
                            <label style={ui.label} htmlFor="observacao">Observação</label>
                            <textarea
                                id="observacao"
                                style={{ ...ui.input, minHeight: 80, resize: 'vertical' }}
                                value={observacao}
                                onChange={e => setObservacao(e.target.value)}
                                disabled={isViewMode || loading}
                            />
                        </div>

                    </div>

                    {/* Rodapé do Modal (Ações) */}
                    <div style={ui.modalFooter}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={ui.buttonLight}
                            disabled={loading}
                        >
                            {isViewMode ? 'Fechar' : 'Cancelar'}
                        </button>

                        {(mode === 'create' || mode === 'edit') && (
                            <button
                                type="submit"
                                style={{ ...ui.buttonPrimary, background: '#a855f7' }}
                                disabled={loading}
                            >
                                {loading ? <FaSyncAlt className="fa-spin" /> : <FaSave />}
                                {mode === 'create' ? ' Cadastrar Processo' : ' Salvar Alterações'}
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ProcessoModal;